import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Timer, RotateCcw, ArrowLeft, Trophy, History, 
  ChevronUp, ChevronDown, Target
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useMobileView } from "@/hooks/use-mobile-view";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function ChronometerPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const isMobile = useMobileView();
  
  // Chronometer state
  const [time, setTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [showTimer, setShowTimer] = useState(true);
  const [attempts, setAttempts] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const hideTimerRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (!token || !savedUser) {
      navigate('/');
      return;
    }
    setUser(JSON.parse(savedUser));
    fetchAttempts();
    fetchLeaderboard();
  }, [navigate]);

  const fetchAttempts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/chronometer/results`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttempts(response.data);
    } catch (e) {
      console.error('Failed to fetch attempts', e);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/chronometer/leaderboard`);
      setLeaderboard(response.data);
    } catch (e) {
      console.error('Failed to fetch leaderboard', e);
    }
  };

  const saveResult = useCallback(async (finalTime, deviation) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/chronometer/results`, {
        score: finalTime,
        deviation: deviation
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Result saved!');
      fetchAttempts();
      fetchLeaderboard();
    } catch (e) {
      toast.error('Failed to save result');
      console.error('Failed to save result', e);
    }
  }, []);

  const handleChronometer = useCallback(() => {
    if (!isRunning) {
      setIsRunning(true);
      setResult(null);
      setShowTimer(true);
      startTimeRef.current = Date.now();
      
      timerRef.current = setInterval(() => {
        const elapsed = (Date.now() - startTimeRef.current) / 1000;
        setTime(elapsed);
      }, 10);
      
      // Hide timer after 3 seconds
      hideTimerRef.current = setTimeout(() => {
        setShowTimer(false);
      }, 3000);
    } else {
      setIsRunning(false);
      setShowTimer(true);
      clearInterval(timerRef.current);
      clearTimeout(hideTimerRef.current);
      const finalTime = (Date.now() - startTimeRef.current) / 1000;
      setTime(finalTime);
      const diff = Math.abs(10 - finalTime);
      setResult(diff);
      saveResult(finalTime, diff);
    }
  }, [isRunning, saveResult]);

  const resetChronometer = () => {
    setTime(0);
    setResult(null);
    setIsRunning(false);
    setShowTimer(true);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
    }
  };

  // Keyboard support
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        handleChronometer();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleChronometer]);

  const getResultMessage = (deviation) => {
    if (deviation < 0.01) return "SUPERHUMAN! You're in the top 0.01%";
    if (deviation < 0.05) return "God-like timing! Elite performance.";
    if (deviation < 0.1) return "Pro level accuracy. Impressive!";
    if (deviation < 0.3) return "Great reflexes! Above average.";
    if (deviation < 0.5) return "Good attempt. Keep practicing!";
    return "Room for improvement. Try again!";
  };

  const getDeviationColor = (deviation) => {
    if (deviation < 0.05) return 'text-emerald-400';
    if (deviation < 0.1) return 'text-green-400';
    if (deviation < 0.3) return 'text-yellow-400';
    if (deviation < 0.5) return 'text-orange-400';
    return 'text-red-400';
  };

  const bestAttempt = attempts.length > 0 
    ? attempts.reduce((best, curr) => curr.deviation < best.deviation ? curr : best)
    : null;

  return (
    <div className="min-h-screen bg-[#020617] text-slate-100">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-[#020617]/80 backdrop-blur-md border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-slate-400 hover:text-white"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Back to Tests</span>
          </Button>
          
          <div className="flex items-center gap-2 font-heading font-bold text-lg">
            <Timer className="w-5 h-5 text-indigo-500" />
            <span>Human Chronometer</span>
          </div>

          <div className="text-sm text-slate-400">
            {user?.username}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className={`grid ${isMobile ? 'grid-cols-1' : 'lg:grid-cols-3'} gap-8`}>
            
            {/* Main Timer Section */}
            <div className={`${isMobile ? 'w-full' : 'lg:col-span-2'}`}>
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-3xl blur opacity-20"></div>
                <div className="relative bg-slate-900 border border-slate-800 rounded-3xl p-8 sm:p-12">
                  
                  {/* Instructions */}
                  <div className="text-center mb-8">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-sm font-medium mb-4">
                      <Target className="w-4 h-4" />
                      Target: 10.000 seconds
                    </div>
                    <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-2">Stop the Clock</h1>
                    <p className="text-slate-400 text-sm sm:text-base">
                      Click START or press SPACE to begin. Stop exactly at 10.000 seconds.
                    </p>
                  </div>

                  {/* Timer Display */}
                  <div className="text-center py-8 sm:py-16">
                    <div 
                      className={`font-mono-display text-7xl sm:text-8xl md:text-9xl font-bold timer-display transition-all duration-300 ${
                        !showTimer && isRunning 
                          ? 'opacity-0 blur-xl' 
                          : isRunning 
                            ? 'text-white animate-pulse' 
                            : result !== null 
                              ? getDeviationColor(result) 
                              : 'text-slate-500'
                      }`}
                      data-testid="timer-display"
                    >
                      {showTimer || !isRunning ? time.toFixed(3) : '?.???'}
                    </div>
                    <div className="text-slate-500 text-lg mt-2 font-mono">
                      {!showTimer && isRunning ? 'timer hidden...' : 'seconds'}
                    </div>
                  </div>

                  {/* Controls */}
                  <div className="flex gap-4 max-w-md mx-auto">
                    <Button 
                      onClick={handleChronometer}
                      className={`flex-1 py-6 sm:py-8 rounded-xl text-xl sm:text-2xl font-heading font-bold uppercase tracking-wider transition-all ${
                        isRunning 
                          ? 'bg-red-500 hover:bg-red-400 shadow-lg shadow-red-500/30' 
                          : 'bg-indigo-600 hover:bg-indigo-500 shadow-lg shadow-indigo-500/30'
                      }`}
                      data-testid="start-stop-btn"
                    >
                      {isRunning ? 'STOP' : 'START'}
                    </Button>
                    {(result !== null || time > 0) && !isRunning && (
                      <Button 
                        onClick={resetChronometer}
                        variant="outline"
                        className="py-6 sm:py-8 px-6 rounded-xl border-slate-700 hover:bg-slate-800"
                        data-testid="reset-btn"
                      >
                        <RotateCcw className="w-6 h-6" />
                      </Button>
                    )}
                  </div>

                  {/* Result Display */}
                  {result !== null && (
                    <div className="mt-10 text-center animate-in fade-in slide-in-from-bottom-4 duration-500">
                      <div className="inline-block bg-slate-800/50 rounded-2xl p-6 sm:p-8 border border-slate-700">
                        <div className="text-slate-400 text-xs uppercase tracking-widest mb-2 font-heading">Your Deviation</div>
                        <div className={`text-4xl sm:text-5xl font-mono-display font-bold ${getDeviationColor(result)}`}>
                          ±{result.toFixed(3)}s
                        </div>
                        <p className="text-slate-400 mt-3 text-sm sm:text-base">{getResultMessage(result)}</p>
                        
                        {result < 0.1 && (
                          <div className="mt-4 flex items-center justify-center gap-2 text-emerald-400">
                            <Trophy className="w-5 h-5" />
                            <span className="font-heading font-bold">Excellent Performance!</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Personal Stats */}
              {bestAttempt && (
                <div className="mt-6 grid grid-cols-3 gap-4">
                  <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 text-center">
                    <div className="text-slate-500 text-xs uppercase tracking-wider font-heading mb-1">Best</div>
                    <div className={`text-xl sm:text-2xl font-mono-display font-bold ${getDeviationColor(bestAttempt.deviation)}`}>
                      ±{bestAttempt.deviation.toFixed(3)}s
                    </div>
                  </div>
                  <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 text-center">
                    <div className="text-slate-500 text-xs uppercase tracking-wider font-heading mb-1">Attempts</div>
                    <div className="text-xl sm:text-2xl font-mono-display font-bold text-white">
                      {attempts.length}
                    </div>
                  </div>
                  <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 text-center">
                    <div className="text-slate-500 text-xs uppercase tracking-wider font-heading mb-1">Average</div>
                    <div className="text-xl sm:text-2xl font-mono-display font-bold text-slate-300">
                      ±{(attempts.reduce((sum, a) => sum + a.deviation, 0) / attempts.length).toFixed(3)}s
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Leaderboard */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  <h3 className="font-heading font-bold text-lg">Global Leaderboard</h3>
                </div>
                <ScrollArea className="h-64">
                  {leaderboard.length > 0 ? (
                    <div className="space-y-2">
                      {leaderboard.slice(0, 10).map((entry, idx) => (
                        <div 
                          key={idx}
                          className={`flex items-center justify-between p-3 rounded-lg ${
                            entry.username === user?.username ? 'bg-indigo-500/20 border border-indigo-500/30' : 'bg-slate-800/50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              idx === 0 ? 'bg-yellow-500 text-black' :
                              idx === 1 ? 'bg-slate-400 text-black' :
                              idx === 2 ? 'bg-amber-700 text-white' :
                              'bg-slate-700 text-slate-300'
                            }`}>
                              {entry.rank}
                            </span>
                            <span className="font-medium truncate max-w-24">{entry.username}</span>
                          </div>
                          <span className={`font-mono-display font-bold ${getDeviationColor(entry.deviation)}`}>
                            ±{entry.deviation.toFixed(3)}s
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-slate-500 py-8">
                      No entries yet. Be the first!
                    </div>
                  )}
                </ScrollArea>
              </div>

              {/* Recent Attempts */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <History className="w-5 h-5 text-indigo-400" />
                  <h3 className="font-heading font-bold text-lg">Your History</h3>
                </div>
                <ScrollArea className="h-64">
                  {attempts.length > 0 ? (
                    <div className="space-y-2">
                      {attempts.slice(0, 15).map((attempt, idx) => {
                        const prevAttempt = attempts[idx + 1];
                        const isImprovement = prevAttempt && attempt.deviation < prevAttempt.deviation;
                        const isWorst = prevAttempt && attempt.deviation > prevAttempt.deviation;
                        
                        return (
                          <div 
                            key={attempt.id}
                            className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50"
                          >
                            <div className="flex items-center gap-2">
                              {isImprovement && <ChevronUp className="w-4 h-4 text-emerald-400" />}
                              {isWorst && <ChevronDown className="w-4 h-4 text-red-400" />}
                              {!isImprovement && !isWorst && <div className="w-4" />}
                              <span className="text-slate-400 text-sm">
                                {new Date(attempt.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            <span className={`font-mono-display font-bold ${getDeviationColor(attempt.deviation)}`}>
                              ±{attempt.deviation.toFixed(3)}s
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center text-slate-500 py-8">
                      No attempts yet. Start testing!
                    </div>
                  )}
                </ScrollArea>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
